import { Component, computed, inject, signal } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { UwTabs, UwTabItem } from '../../shared/ui/tabs/uw-tabs';
import { UwAccountHeader } from '../../shared/domain/account-header/uw-account-header';
import { AccountStore } from '../../core/stores/account.store';
import { ClassicProfilePage, ClassicRiskPage, ClassicPricingPage, ClassicReferralsPage, ClassicDocumentsPage, ClassicQuotePage } from './account-pages';
import type { Account } from '../../core/models/account.model';

@Component({
  selector: 'uw-classic-account-page',
  standalone: true,
  imports: [
    UwTabs,
    UwAccountHeader,
    ClassicProfilePage,
    ClassicRiskPage,
    ClassicPricingPage,
    ClassicReferralsPage,
    ClassicDocumentsPage,
    ClassicQuotePage,
  ],
  template: `
    <div class="mx-auto max-w-6xl space-y-4 p-4 sm:p-6">
      <uw-account-header [account]="account()" />

      <uw-tabs [tabs]="tabs" [active]="section()" (activeChange)="selectSection($event)" />

      <div>
        @switch (section()) {
          @case ('risk') {
            <uw-classic-risk-page />
          }
          @case ('pricing') {
            <uw-classic-pricing-page />
          }
          @case ('referrals') {
            <uw-classic-referrals-page />
          }
          @case ('documents') {
            <uw-classic-documents-page />
          }
          @case ('quote') {
            <uw-classic-quote-page />
          }
          @default {
            <uw-classic-profile-page />
          }
        }
      </div>
    </div>
  `,
})
export class ClassicAccountPage {
  private readonly accountStore = inject(AccountStore);
  private readonly route = inject(ActivatedRoute);
  private readonly router = inject(Router);

  protected readonly account = computed<Account | null>(() => this.accountStore.selectedAccount() ?? null);

  protected readonly tabs: UwTabItem[] = [
    { id: 'profile', label: 'Profile', icon: 'building' },
    { id: 'risk', label: 'Risk', icon: 'gauge' },
    { id: 'pricing', label: 'Pricing', icon: 'calculator' },
    { id: 'referrals', label: 'Referrals', icon: 'arrow-up' },
    { id: 'documents', label: 'Documents', icon: 'file-check' },
    { id: 'quote', label: 'Quote', icon: 'shield-check' },
  ];

  protected readonly section = signal('profile');

  private readonly accountId = signal<string | null>(null);

  constructor() {
    this.route.paramMap.subscribe((params) => {
      const id = params.get('id');
      this.accountId.set(id);
      if (id) {
        this.accountStore.selectAccount(id);
      }
    });
    this.route.queryParamMap.subscribe((params) => {
      const initial = params.get('section');
      if (initial && this.tabs.some((t) => t.id === initial)) {
        this.section.set(initial);
      }
    });
  }

  protected selectSection(id: string): void {
    this.section.set(id);
    void this.router.navigate([], { relativeTo: this.route, queryParams: { section: id } });
  }
}
